import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, ExternalLink, AlertCircle } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import PublicPage from "./PublicPage";

const INQUIRY_TYPES = ["Cruise", "Group Trip", "Other Travel", "Becoming a Travel Agent"];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", inquiry_type: "Cruise", message: "", company: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your full name.";
    if (!form.email.trim()) errs.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (!form.message.trim()) errs.message = "Please tell Lisa a little about your trip.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;
    setStatus("sending");
    try {
      await api.post("/public/inquiries", form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", inquiry_type: "Cruise", message: "", company: "" });
    } catch (err) {
      setServerError(fmtErr(err, "Your message could not be sent. Please try again or email Lisa directly."));
      setStatus("error");
    }
  };

  const inputCls = (bad) =>
    `w-full rounded-xl border ${bad ? "border-red-400" : "border-slate-300"} bg-white px-4 py-3 text-[15px] text-slate-800 placeholder-slate-400 focus:border-amber-500 transition-colors`;

  return (
    <div data-testid="contact-page">
      <PublicPage slug="contact" />

      <section className="section-pad !pt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-10 items-start">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="lg:col-span-3">
            <div className="card-luxe p-7 sm:p-9">
              <h2 className="font-heading text-3xl text-[#0B1D3A] font-semibold mb-2">Send Lisa a Message</h2>
              <p className="text-slate-500 text-sm mb-7">Tell her what you're dreaming of and she'll be in touch.</p>

              {status === "success" ? (
                <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-8 text-center" data-testid="inquiry-form-success-alert" role="status">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" aria-hidden="true" />
                  <h3 className="font-heading text-2xl text-emerald-900 font-semibold">Message received!</h3>
                  <p className="text-emerald-800/80 text-sm mt-2">Thank you — your inquiry has been saved and Lisa will get back to you soon.</p>
                  <button onClick={() => setStatus("idle")} data-testid="inquiry-send-another-button" className="btn-outline !text-[#0B1D3A] mt-6 !py-2.5 !px-5 text-sm">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate data-testid="contact-inquiry-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="cf-name" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Full name *</label>
                      <input id="cf-name" data-testid="inquiry-form-name-input" className={inputCls(errors.name)} value={form.name} onChange={set("name")} autoComplete="name" aria-invalid={!!errors.name} aria-describedby={errors.name ? "cf-name-err" : undefined} />
                      {errors.name && <p id="cf-name-err" role="alert" className="text-red-600 text-xs mt-1.5 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="cf-email" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Email address *</label>
                      <input id="cf-email" type="email" data-testid="inquiry-form-email-input" className={inputCls(errors.email)} value={form.email} onChange={set("email")} autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? "cf-email-err" : undefined} />
                      {errors.email && <p id="cf-email-err" role="alert" className="text-red-600 text-xs mt-1.5 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="cf-phone" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Phone number <span className="font-normal text-slate-400">(optional)</span></label>
                      <input id="cf-phone" type="tel" data-testid="inquiry-form-phone-input" className={inputCls(false)} value={form.phone} onChange={set("phone")} autoComplete="tel" />
                    </div>
                    <div>
                      <label htmlFor="cf-type" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Inquiry type *</label>
                      <select id="cf-type" data-testid="inquiry-form-type-select" className={inputCls(false)} value={form.inquiry_type} onChange={set("inquiry_type")}>
                        {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mt-5">
                    <label htmlFor="cf-message" className="block text-sm font-semibold text-[#0B1D3A] mb-1.5">Message *</label>
                    <textarea id="cf-message" rows={5} data-testid="inquiry-form-message-textarea" className={inputCls(errors.message)} value={form.message} onChange={set("message")} placeholder="Where would you like to go, and who is traveling?" aria-invalid={!!errors.message} aria-describedby={errors.message ? "cf-message-err" : undefined} />
                    {errors.message && <p id="cf-message-err" role="alert" className="text-red-600 text-xs mt-1.5 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.message}</p>}
                  </div>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="cf-company">Company</label>
                    <input id="cf-company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
                  </div>
                  {serverError && (
                    <div role="alert" data-testid="inquiry-form-error-alert" className="mt-5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {serverError}
                    </div>
                  )}
                  <button type="submit" disabled={status === "sending"} data-testid="inquiry-form-submit-button" className="btn-brand mt-7 w-full sm:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed">
                    {status === "sending" ? "Sending…" : <>Send Message <Send className="w-4 h-4" aria-hidden="true" /></>}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          <motion.aside initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-2 space-y-6">
            <div className="bg-[#0B1D3A] rounded-2xl p-7 text-slate-200 border border-amber-500/20">
              <h3 className="font-heading text-2xl text-amber-50 font-semibold mb-3">Prefer to browse first?</h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-5">
                Explore cruises, group trips, and more on Lisa's main travel website — then come back here anytime with questions.
              </p>
              <a href="https://www.evotravelagent.com/ldixontravel" target="_blank" rel="noopener noreferrer" data-testid="contact-visit-website-button" className="btn-brand w-full justify-center">
                Visit My Travel Website <ExternalLink className="w-4 h-4" aria-hidden="true" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>
            <div className="card-luxe p-6 text-sm text-slate-500 leading-relaxed">
              <p className="font-semibold text-[#0B1D3A] mb-1.5">A quick note</p>
              Submitting this form sends your message directly to Lisa — no account needed. She typically replies by email.
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  );
}
